import apiClient from './apiClient';
import { isRecord, normalizeCount, normalizeString } from './normalize';
import type {
  StatisticsOverview,
  StatsGranularity,
  StatsMetric,
  TimeSeriesPoint,
  TimeSeriesResponse,
} from './types';

/**
 * Service fuer die Statistik-Endpunkte des MVGen-Management-Backends
 * (GET /api/statistics/overview und GET /api/statistics/<metric>).
 *
 * Kein Caching (Projektstandard): Jeder Aufruf geht direkt ans Backend.
 *
 * Alle Antworten werden defensiv normalisiert, statt sie ungeprueft als
 * DTO durchzureichen. Eine unvollstaendige Antwort (fehlendes points-Array,
 * null-Werte, Strings statt Zahlen) wuerde sonst erst tief im Chart
 * auffallen — im Zwei-Serien-Chart als Render-Crash.
 */

/** Abfrage-Parameter fuer eine Statistik-Zeitreihe. */
export interface TimeSeriesQuery {
  /** Zeitliche Aggregationsstufe (day | week | month | year). */
  readonly granularity: StatsGranularity;
  /**
   * Beginn des Zeitraums (YYYY-MM-DD). Weglassen aktiviert den
   * Server-Default (day: 30 Tage, week: 26 Wochen, month: 24 Monate,
   * year: 10 Jahre).
   */
  readonly from?: string;
  /** Ende des Zeitraums (YYYY-MM-DD); weglassen = Server-Default. */
  readonly to?: string;
}

/** Bekannte Metriken (Laufzeit-Pendant zu StatsMetric). */
const STATS_METRICS: readonly StatsMetric[] = [
  'user-registrations',
  'user-totals',
  'properties-created',
  'contracts-created',
  'contracts-finalized',
];

/** Bekannte Granularitaeten (Laufzeit-Pendant zu StatsGranularity). */
const STATS_GRANULARITIES: readonly StatsGranularity[] = ['day', 'week', 'month', 'year'];

/*
 * isRecord/normalizeCount/normalizeString stammen aus ./normalize und
 * werden von allen Services geteilt (siehe dortiger Kommentar).
 */

/** Normalisiert die Overview-Antwort auf vier garantierte Zahlen. */
function normalizeOverview(payload: unknown): StatisticsOverview {
  const raw: Record<string, unknown> = isRecord(payload) ? payload : {};
  return {
    totalUsers: normalizeCount(raw.totalUsers),
    totalProperties: normalizeCount(raw.totalProperties),
    totalContracts: normalizeCount(raw.totalContracts),
    totalContractsFinalized: normalizeCount(raw.totalContractsFinalized),
  };
}

/**
 * Prueft einen einzelnen Datenpunkt auf {periodStart: string, value: number}.
 * Ungueltige Punkte liefern null und werden von der Zeitreihe verworfen.
 */
function normalizePoint(payload: unknown): TimeSeriesPoint | null {
  if (!isRecord(payload)) {
    return null;
  }
  const periodStart: unknown = payload.periodStart;
  const value: unknown = payload.value;
  if (typeof periodStart !== 'string' || periodStart === '') {
    return null;
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  return { periodStart, value };
}

/**
 * Normalisiert eine Zeitreihen-Antwort: points ist danach IMMER ein Array
 * gueltiger Punkte, metric/granularity sind IMMER bekannte Union-Werte
 * (Fallback: die angefragten Parameter).
 */
function normalizeTimeSeries(
  payload: unknown,
  metric: StatsMetric,
  query: TimeSeriesQuery
): TimeSeriesResponse {
  const raw: Record<string, unknown> = isRecord(payload) ? payload : {};
  const rawMetric: unknown = raw.metric;
  const rawGranularity: unknown = raw.granularity;
  const points: readonly TimeSeriesPoint[] = Array.isArray(raw.points)
    ? raw.points
        .map((point: unknown) => normalizePoint(point))
        .filter((point): point is TimeSeriesPoint => point !== null)
    : [];
  return {
    metric: STATS_METRICS.some((known) => known === rawMetric)
      ? (rawMetric as StatsMetric)
      : metric,
    granularity: STATS_GRANULARITIES.some((known) => known === rawGranularity)
      ? (rawGranularity as StatsGranularity)
      : query.granularity,
    from: normalizeString(raw.from, query.from ?? ''),
    to: normalizeString(raw.to, query.to ?? ''),
    points,
  };
}

/**
 * Laedt die Overview-Kennzahlen fuer die Dashboard-Kacheln.
 */
export async function fetchOverview(signal?: AbortSignal): Promise<StatisticsOverview> {
  const response = await apiClient.get<unknown>('/api/statistics/overview', {
    signal,
  });
  return normalizeOverview(response.data);
}

/**
 * Laedt eine Statistik-Zeitreihe fuer die angegebene Metrik.
 * from/to werden nur mitgesendet, wenn sie gesetzt sind, damit die
 * Server-Defaults greifen koennen.
 */
export async function fetchTimeSeries(
  metric: StatsMetric,
  query: TimeSeriesQuery,
  signal?: AbortSignal
): Promise<TimeSeriesResponse> {
  const params: Record<string, string> = { granularity: query.granularity };
  if (query.from !== undefined) {
    params.from = query.from;
  }
  if (query.to !== undefined) {
    params.to = query.to;
  }
  const response = await apiClient.get<unknown>(`/api/statistics/${metric}`, {
    params,
    signal,
  });
  return normalizeTimeSeries(response.data, metric, query);
}
